import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articleMap, getRelatedArticles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRelatedArticles(article.slug, article.category, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://jiejiefriends.com/article/${article.slug}`,
    image: article.image,
    articleSection: article.categoryName,
    keywords: article.tags.join(', '),
    wordCount: article.content.join('').length,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '姐姐的朋友',
      url: 'https://jiejiefriends.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-12 md:py-16">
              <div className="mb-4">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: article.categoryName, href: `/category/${article.category}` },
                    { label: article.title },
                  ]}
                />
              </div>
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {article.categoryName}
                </Link>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="mt-4 flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mt-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="py-8 md:py-12">
              {article.content.map((paragraph, i) => (
                <p
                  key={i}
                  className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="py-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="py-6 border-t border-background-200/70 flex items-center gap-4">
              <span className="text-sm text-foreground-500">分享：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微信">
                <i className="ri-wechat-line text-sm"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微博">
                <i className="ri-weibo-line text-sm"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap" aria-label="复制链接">
                <i className="ri-link text-sm"></i>
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-20">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden transition-all hover:border-primary-200 cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-1.5">
                        <span>{ra.date}</span>
                        <span>·</span>
                        <span>{ra.readTime}</span>
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}